import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronDown,
  Search,
  Menu,
  X,
  ShoppingCart,
  User,
  LogOut,
} from "lucide-react";
import { useContext } from "react";
import { ProductContext } from "./context/ProductContext";
import { useAuth } from "./context/AuthContext";
import { useCategories } from "./context/CategoriesContext";

const Header = () => {
  const { product } = useContext(ProductContext);
  const { categories, loading, error } = useCategories();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  // Search functionality states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [allProducts, setAllProducts] = useState([]);

  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const desktopSearchRef = useRef(null);

  const { user, logout } = useAuth();

  // Helper function to check if a route is active
  const isActiveRoute = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  // Helper function to check if any category route is active
  const isCategoryRouteActive = () => {
    return (
      location.pathname.startsWith("/category/") ||
      location.pathname === "/categories"
    );
  };

  // Enhanced navigation function with scroll to top
  const handleNavigation = (path) => {
    navigate(path);
    // Scroll to top after navigation
    window.scrollTo(0, 0);
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
    setIsSearchOpen(false);
    setShowSearchDropdown(false);
    setSearchQuery("");
  };

  const [productId, setProductId] = useState(null);

  useEffect(() => {
    const savedId = localStorage.getItem("activeProductId");
    setProductId(savedId);
  }, []);

  // Fetch all products on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(
          "https://acsdev.in/manaprint_backend/public/api/client/products",
        );
        const data = await response.json();
        if (data.status === "200" && data.data) {
          setAllProducts(data.data);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, []);

  // Debounced search function
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        performSearch(searchQuery);
      } else {
        setSearchResults([]);
        setShowSearchDropdown(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, allProducts]);

  const performSearch = (query) => {
    setIsSearchLoading(true);

    const filteredProducts = allProducts.filter(
      (product) =>
        product.product_name.toLowerCase().includes(query.toLowerCase()) ||
        product.description.toLowerCase().includes(query.toLowerCase()) ||
        product.category_name.toLowerCase().includes(query.toLowerCase()),
    );

    setSearchResults(filteredProducts.slice(0, 8)); // Limit to 8 results
    setShowSearchDropdown(true); // Always show dropdown when searching
    setIsSearchLoading(false);
  };

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Enhanced product selection with scroll to top
  const handleProductSelect = (productId) => {
    handleNavigation(`/product/${productId}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchResults.length === 1) {
      handleProductSelect(searchResults[0].id);
    } else if (searchResults.length > 1) {
      // Show dropdown if multiple results
      setShowSearchDropdown(true);
    }
  };

  const toggleMobileMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Mobile menu toggle clicked, current state:", isMobileMenuOpen);
    setIsMobileMenuOpen((prev) => !prev);
    setIsSearchOpen(false);
    setIsCategoryOpen(false);
    setShowSearchDropdown(false);
  };

  const toggleSearch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSearchOpen((prev) => !prev);
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
    if (!isSearchOpen) {
      setShowSearchDropdown(false);
      setSearchQuery("");
    }
  };

  const toggleCategory = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCategoryOpen((prev) => !prev);
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
    setShowSearchDropdown(false);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close menus when clicking outside or on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
        setIsSearchOpen(false);
        setIsCategoryOpen(false);
        setShowSearchDropdown(false);
      }
    };

    const handleClickOutside = (e) => {
      // Only close if clicking outside the menu containers
      if (
        isMobileMenuOpen &&
        !e.target.closest(".mobile-menu-container") &&
        !e.target.closest(".mobile-menu-toggle")
      ) {
        setIsMobileMenuOpen(false);
      }
      if (isCategoryOpen && !e.target.closest(".category-dropdown-container")) {
        setIsCategoryOpen(false);
      }
      if (
        showSearchDropdown &&
        !e.target.closest(".search-container") &&
        !e.target.closest(".search-icon-button")
      ) {
        setShowSearchDropdown(false);
      }
      if (
        isSearchOpen &&
        !e.target.closest(".search-container") &&
        !e.target.closest(".search-icon-button")
      ) {
        setIsSearchOpen(false);
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isMobileMenuOpen, isCategoryOpen, showSearchDropdown, isSearchOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // Enhanced logout with scroll to top
  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    if (window.confirm("Are you sure you want to logout?")) {
      logout();
      navigate("/signin", { replace: true });
      // Scroll to top after logout navigation
      window.scrollTo(0, 0);
    }
  };

  // Debug log for mobile menu state
  useEffect(() => {
    console.log("Mobile menu state changed:", isMobileMenuOpen);
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav
        className="sticky top-0 left-0 right-0 w-full shadow-lg z-50 overflow-visible bg-[#3a3938]"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex justify-between items-center w-full px-4 md:px-6 lg:px-14 py-2  mx-auto">
          {/* Left Navigation - Desktop Only */}
          <div className="hidden lg:flex items-center gap-8">
            <button
              onClick={() => handleNavigation("/products")}
              className={`text-sm font-medium transition-all duration-200 py-2 px-4 rounded-lg ${
                isActiveRoute("/products")
                  ? "bg-orange-50 text-teal-800 shadow-md border-2 border-orange-200"
                  : "text-orange-50 hover:bg-orange-50 hover:text-teal-600"
              }`}
            >
              Products
            </button>
            <button
              onClick={() => handleNavigation("/blog")}
              className={`text-sm font-medium transition-all duration-200 py-2 px-4 rounded-lg ${
                isActiveRoute("/blog")
                  ? "bg-orange-50 text-teal-800 shadow-md border-2 border-orange-200"
                  : "text-orange-50 hover:bg-orange-50 hover:text-teal-600"
              }`}
            >
              Blog
            </button>
            <button
              onClick={() => handleNavigation("/contact")}
              className={`text-sm font-medium transition-all duration-200 py-2 px-4 rounded-lg ${
                isActiveRoute("/contact")
                  ? "bg-orange-50 text-teal-800 shadow-md border-2 border-orange-200"
                  : "text-orange-50 hover:bg-orange-50 hover:text-teal-600"
              }`}
            >
              Contact Us
            </button>
          </div>

          {/* Center - Logo */}
          <div className="flex-shrink-0 relative lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2">
            <button
              onClick={() => handleNavigation("/")}
              className={`hover:opacity-100 transition-all mt-1 duration-300 relative`}
              aria-label="Manaprint Home"
            >
              <img
                src="https://cdn.acsdev.in/ManaPrint/manaprint_logo_tras.png"
                alt="Manaprint Logo"
                className="h-12 sm:h-14 md:h-16 lg:h-22 xl:h-20 w-auto scale-110 object-contain rounded-2xl"
              />
            </button>
          </div>

          {/* Right - Icons (Desktop) */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Desktop Search Container */}
            <div className="flex items-center gap-3 relative">
              {/* Search Bar - appears beside search icon */}
              <div
                className={`transition-all duration-300 ease-in-out ${
                  isSearchOpen
                    ? "max-w-md opacity-100"
                    : "max-w-0 opacity-0 overflow-hidden"
                }`}
              >
                <div
                  className="relative search-container"
                  ref={desktopSearchRef}
                >
                  <form
                    onSubmit={handleSearchSubmit}
                    className="flex gap-3 py-2 pr-4 pl-6 text-base border-2 border-gray-600 rounded-full hover:border-orange-50 focus-within:border-orange-50 transition-all duration-200 w-80 bg-gray-800"
                  >
                    <input
                      type="search"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      className="bg-transparent border-none outline-none flex-1 min-w-0 placeholder-gray-400 text-orange-50"
                      aria-label="Search products"
                      autoFocus={isSearchOpen}
                    />
                    <button
                      type="submit"
                      aria-label="Search"
                      className="focus:outline-none transition-colors duration-200 flex-shrink-0 hover:text-orange-50 text-gray-400"
                    >
                      <Search size={18} />
                    </button>
                  </form>
                </div>
              </div>

              {/* Search Icon */}
              <button
                onClick={toggleSearch}
                className="search-icon-button w-12 h-12 rounded-xl bg-teal-800 hover:bg-teal-900 flex items-center justify-center transition-all duration-200 transform hover:scale-105 flex-shrink-0"
                aria-label="Toggle search"
              >
                {isSearchOpen ? (
                  <X size={20} className="text-white" />
                ) : (
                  <Search size={20} className="text-white" />
                )}
              </button>

              {/* Desktop Search Dropdown - moved outside the animated container */}
              {showSearchDropdown && isSearchOpen && (
                <div className="absolute top-full right-16 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl z-[9999] max-h-80 overflow-y-auto w-80">
                  {isSearchLoading ? (
                    <div className="p-4 text-center text-gray-500">
                      <div className="inline-flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                        Searching...
                      </div>
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div className="py-2">
                      {searchResults.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => handleProductSelect(product.id)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-800 text-sm">
                                {product.product_name}
                              </h4>
                              <p className="text-xs text-gray-500 mt-1">
                                {product.category_name}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : searchQuery.trim() ? (
                    <div className="p-6 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <Search size={24} className="text-gray-300" />
                        <p className="text-sm font-medium">No products found</p>
                        <p className="text-xs text-gray-400">
                          Try searching with different keywords
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Cart Icon */}
            {user ? (
              <button
                onClick={() => handleNavigation("/cart")}
                className={`relative w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 transform hover:scale-105 shadow-lg ${
                  isActiveRoute("/cart")
                    ? "bg-teal-100 border-2 border-teal-300"
                    : "hover:bg-orange-50"
                }`}
                style={{
                  backgroundColor: isActiveRoute("/cart")
                    ? "#ccfbf1"
                    : "#fff7ed",
                }}
                aria-label="Shopping Cart"
              >
                <ShoppingCart
                  size={22}
                  className={
                    isActiveRoute("/cart") ? "text-teal-700" : "text-teal-800"
                  }
                />
              </button>
            ) : (
              <></>
            )}

            {/* Profile Icon */}
            {user ? (
              <button
                onClick={() => handleNavigation("/profile")}
                className={`relative w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 transform hover:scale-105 shadow-lg ${
                  isActiveRoute("/profile")
                    ? "bg-teal-100 border-2 border-teal-300"
                    : "hover:bg-orange-50"
                }`}
                style={{
                  backgroundColor: isActiveRoute("/profile")
                    ? "#ccfbf1"
                    : "#fff7ed",
                }}
                aria-label="Profile"
              >
                <User
                  size={22}
                  className={
                    isActiveRoute("/profile")
                      ? "text-teal-700"
                      : "text-teal-800"
                  }
                />
              </button>
            ) : (
              <></>
            )}

            {/* Auth Button */}
            {user ? (
              <button
                onClick={handleLogout}
                className="relative w-12 h-12 rounded-xl hover:bg-orange-50 flex items-center justify-center transition-all duration-200 transform hover:scale-105 shadow-lg"
                style={{ backgroundColor: "#fff7ed" }}
                aria-label="Logout"
              >
                <LogOut size={22} className="text-teal-800" />
              </button>
            ) : (
              <button
                onClick={() => handleNavigation("/signin")}
                className={`px-6 py-3 text-sm font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 whitespace-nowrap border ${
                  isActiveRoute("/signin") || isActiveRoute("/signup")
                    ? "text-white bg-teal-600 border-teal-600"
                    : "text-gray-800 bg-orange-50 hover:bg-orange-50 border-orange-50"
                }`}
              >
                Sign In
              </button>
            )}
          </div>

          {/* Tablet Controls (sm to lg) */}
          <div className="hidden sm:flex lg:hidden items-center gap-3 flex-shrink-0">
            {/* Tablet Search Container */}
            <div className="flex items-center gap-3 relative">
              {/* Search Bar - appears beside search icon */}
              <div
                className={`transition-all duration-300 ease-in-out ${
                  isSearchOpen
                    ? "max-w-md opacity-100"
                    : "max-w-0 opacity-0 overflow-hidden"
                }`}
              >
                <div
                  className="relative search-container"
                  ref={desktopSearchRef}
                >
                  <form
                    onSubmit={handleSearchSubmit}
                    className="flex gap-3 py-2 pr-4 pl-6 text-base border-2 border-gray-600 rounded-full hover:border-orange-50 focus-within:border-orange-50 transition-all duration-200 w-80 bg-gray-800"
                  >
                    <input
                      type="search"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      className="bg-transparent border-none outline-none flex-1 min-w-0 placeholder-gray-400 text-orange-50"
                      aria-label="Search products"
                      autoFocus={isSearchOpen}
                    />
                    <button
                      type="submit"
                      aria-label="Search"
                      className="focus:outline-none transition-colors duration-200 flex-shrink-0 hover:text-orange-50 text-gray-400"
                    >
                      <Search size={18} />
                    </button>
                  </form>
                </div>
              </div>

              {/* Search Icon */}
              <button
                onClick={toggleSearch}
                className="search-icon-button w-12 h-12 rounded-xl bg-teal-800 hover:bg-teal-900 flex items-center justify-center transition-all duration-200 transform hover:scale-105 flex-shrink-0"
                aria-label="Toggle search"
              >
                {isSearchOpen ? (
                  <X size={20} className="text-white" />
                ) : (
                  <Search size={20} className="text-white" />
                )}
              </button>

              {/* Tablet Search Dropdown */}
              {showSearchDropdown && isSearchOpen && (
                <div className="absolute top-full right-16 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl z-[9999] max-h-80 overflow-y-auto w-80">
                  {isSearchLoading ? (
                    <div className="p-4 text-center text-gray-500">
                      <div className="inline-flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                        Searching...
                      </div>
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div className="py-2">
                      {searchResults.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => handleProductSelect(product.id)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-800 text-sm">
                                {product.product_name}
                              </h4>
                              <p className="text-xs text-gray-500 mt-1">
                                {product.category_name}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : searchQuery.trim() ? (
                    <div className="p-6 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <Search size={24} className="text-gray-300" />
                        <p className="text-sm font-medium">No products found</p>
                        <p className="text-xs text-gray-400">
                          Try searching with different keywords
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle for Tablet */}
            <button
              onClick={toggleMobileMenu}
              className="mobile-menu-toggle w-10 h-10 rounded-xl bg-[#257875] hover:[#257875] flex items-center justify-center transition-all duration-200 transform hover:scale-105"
              aria-label="Toggle navigation menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X size={22} className="text-white" />
              ) : (
                <Menu size={22} className="text-white" />
              )}
            </button>
          </div>

          {/* Mobile Controls */}
          <div className="flex sm:hidden items-center gap-3 flex-shrink-0">
            {/* Mobile Search Toggle */}
            <button
              onClick={toggleSearch}
              className="w-10 h-10 rounded-xl bg-teal-800 hover:bg-teal-900 flex items-center justify-center transition-all duration-200 transform hover:scale-105"
              aria-label="Toggle search"
            >
              {isSearchOpen ? (
                <X size={20} className="text-white" />
              ) : (
                <Search size={20} className="text-white" />
              )}
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={toggleMobileMenu}
              className="mobile-menu-toggle w-10 h-10 rounded-xl bg-[#257875] hover:[#257875] flex items-center justify-center transition-all duration-200 transform hover:scale-105"
              aria-label="Toggle navigation menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X size={22} className="text-white" />
              ) : (
                <Menu size={22} className="text-white" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        <div
          className={`sm:hidden w-full px-4 transition-all duration-300 ease-in-out relative ${
            isSearchOpen
              ? "max-h-96 opacity-100 pb-4"
              : "max-h-0 opacity-0 overflow-hidden"
          }`}
        >
          <div className="relative search-container" ref={mobileSearchRef}>
            <form
              onSubmit={handleSearchSubmit}
              className="flex gap-3 py-3 pr-4 pl-6 text-base border-2 border-gray-600 rounded-full hover:border-orange-50 focus-within:border-orange-50 transition-all duration-200 w-full max-w-md mx-auto bg-gray-800"
            >
              <input
                type="search"
                placeholder="Search products..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                className="bg-transparent border-none outline-none flex-1 min-w-0 placeholder-gray-400 text-orange-50"
                aria-label="Search products"
                autoFocus={isSearchOpen}
              />
              <button
                type="submit"
                aria-label="Search"
                className="focus:outline-none transition-colors duration-200 flex-shrink-0 hover:text-orange-50 text-gray-400"
              >
                <Search size={20} />
              </button>
            </form>

            {/* Mobile Search Dropdown */}
            {showSearchDropdown && isSearchOpen && (
              <div className="absolute top-full left-0 right-0 mt-4 bg-white border border-gray-200 rounded-xl shadow-2xl z-[9999] max-h-80 overflow-y-auto">
                {isSearchLoading ? (
                  <div className="p-4 text-center text-gray-500">
                    <div className="inline-flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                      Searching...
                    </div>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="py-2">
                    {searchResults.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => handleProductSelect(product.id)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-800 text-sm">
                              {product.product_name}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">
                              {product.category_name}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : searchQuery.trim() ? (
                  <div className="p-6 text-center text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <Search size={24} className="text-gray-300" />
                      <p className="text-sm font-medium">No products found</p>
                      <p className="text-xs text-gray-400">
                        Try searching with different keywords
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300">
          {/* Mobile Menu Panel */}
          <div className="mobile-menu-container fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out overflow-y-auto translate-x-0">
            <div className="flex flex-col h-full">
              {/* Mobile Menu Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-emerald-800">
                <h2 className="text-xl font-bold text-orange-50">Menu</h2>
                <button
                  onClick={closeMobileMenu}
                  className="w-8 h-8 rounded-full bg-orange-50 bg-opacity-80 hover:bg-opacity-30 flex items-center justify-center transition-colors duration-200"
                  aria-label="Close menu"
                >
                  <X size={20} className="text-gray-800" />
                </button>
              </div>

              {/* Mobile Menu Items */}
              <div className="flex flex-col py-4 flex-1 bg-orange-50 bg-opacity-90">
                <button
                  onClick={() => handleNavigation("/")}
                  className={`flex items-center px-6 py-4 text-left transition-colors duration-200 ${
                    isActiveRoute("/")
                      ? "text-emerald-800 bg-emerald-50 border-l-4 border-emerald-800"
                      : "text-gray-800  hover:bg-gray-50"
                  }`}
                >
                  <span className="text-base font-medium">Home</span>
                </button>

                <button
                  onClick={() => handleNavigation("/products")}
                  className={`flex items-center px-6 py-4 text-left transition-colors duration-200 ${
                    isActiveRoute("/products")
                      ? "text-emerald-800 bg-emerald-50 border-l-4 border-emerald-800"
                      : "text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  <span className="text-base">Products</span>
                </button>
                <button
                  onClick={() => handleNavigation("/blog")}
                  className={`flex items-center px-6 py-4 text-left transition-colors duration-200 ${
                    isActiveRoute("/blog")
                      ? "text-emerald-800 bg-emerald-50 border-l-4 border-emerald-800"
                      : "text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  <span className="text-base">Blog</span>
                </button>
                <button
                  onClick={() => handleNavigation("/contact")}
                  className={`flex items-center px-6 py-4 text-left transition-colors duration-200 ${
                    isActiveRoute("/contact")
                      ? "text-emerald-800 bg-emerald-50 border-l-4 border-emerald-800"
                      : "text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  <span className="text-base">Contact Us</span>
                </button>

                {/* Mobile Category Section */}
                {/* <div className="px-6 py-4">
                  <div className={`text-base font-medium mb-3 ${
                    isCategoryRouteActive() ? 'text-teal-700' : 'text-gray-800'
                  }`}>
                    Categories
                  </div>
                  <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                    {categories.slice(0, 8).map((category) => (
                      <button
                        key={category.id}
                        onClick={() => handleNavigation(`/category/${category.id}`)}
                        className={`text-left text-sm py-2 pl-4 transition-colors duration-200 rounded-md border-l-2 ${
                          location.pathname === `/category/${category.id}`
                            ? 'text-teal-700 bg-teal-50 border-teal-500 font-medium'
                            : 'text-gray-600 hover:text-teal-600 border-transparent hover:bg-orange-50 hover:border-orange-50'
                        }`}
                      >
                        {category.title}
                      </button>
                    ))}
                  </div>
                  {categories.length > 8 && (
                    <button
                      onClick={() => handleNavigation('/categories')}
                      className={`text-sm font-medium mt-3 pl-4 transition-colors duration-200 block ${
                        location.pathname === '/categories'
                          ? 'text-teal-700'
                          : 'text-teal-600 hover:text-teal-700'
                      }`}
                    >
                      View All Categories →
                    </button>
                  )}
                </div> */}

                {user ? (
                  <button
                    onClick={() => handleNavigation("/cart")}
                    className={`flex items-center px-6 py-4 text-left transition-colors duration-200 ${
                      isActiveRoute("/cart")
                        ? "text-teal-700 bg-teal-50 border-l-4 border-teal-500"
                        : "text-gray-800 hover:bg-gray-50"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                        isActiveRoute("/cart") ? "bg-teal-500" : "bg-green-500"
                      }`}
                    >
                      <ShoppingCart size={16} className="text-white" />
                    </div>
                    <span className="text-base">Cart</span>
                  </button>
                ) : (
                  <></>
                )}

                {user ? (
                  <button
                    onClick={() => handleNavigation("/profile")}
                    className={`flex items-center px-6 py-4 text-left transition-colors duration-200 ${
                      isActiveRoute("/profile")
                        ? "text-teal-700 bg-teal-50 border-l-4 border-teal-500"
                        : "text-gray-800 hover:bg-gray-50"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                        isActiveRoute("/profile")
                          ? "bg-teal-500"
                          : "bg-blue-500"
                      }`}
                    >
                      <User size={16} className="text-white" />
                    </div>
                    <span className="text-base">Profile</span>
                  </button>
                ) : (
                  <></>
                )}
              </div>

              {/* Mobile Menu Footer */}
              <div className="p-6 border-t border-emerald-700/50 bg-orange-50 bg-opacity-90">
                {user ? (
                  <button
                    onClick={handleLogout}
                    className="w-full flex justify-center items-center px-6 py-3 text-base font-semibold text-white bg-red-500 hover:bg-red-600 rounded-xl shadow-lg transition-all duration-200"
                  >
                    <LogOut size={20} className="mr-2" />
                    Logout
                  </button>
                ) : (
                  <button
                    onClick={() => handleNavigation("/signin")}
                    className={`w-full flex justify-center items-center px-6 py-3 text-base font-semibold rounded-xl shadow-lg transition-all duration-200 border ${
                      isActiveRoute("/signin") || isActiveRoute("/signup")
                        ? "text-white bg-teal-600 border-teal-600"
                        : "text-gray-800 bg-orange-50 hover:bg-orange-50 border-orange-50"
                    }`}
                  >
                    Sign In
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Mega Menu Backdrop */}
      {isCategoryOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-20 z-40"
          onClick={() => setIsCategoryOpen(false)}
        />
      )}
    </>
  );
};

export default Header;
